import type { Node as ProsemirrorNode } from 'prosemirror-model'
import { buildTemplateToken, replaceTemplateTokens, sanitizeFallback } from '@paintscout/util/templater'
import { formatPlaceholderLabel } from './formatPlaceholderLabel'

export const ZERO_WIDTH_SPACE = '\u200B'

export interface PlaceholderRange {
  start: number
  end: number
  key: string
  label?: string | null
  fallback?: string | null
}

/**
 * Builds the HTML for a single placeholder span plus its trailing zero-width
 * space. The ZWS gives the caret a landing spot just outside the placeholder,
 * and `contenteditable="false"` makes the span behave like an atomic chip.
 *
 * This is the single source of truth for placeholder markup — every place that
 * serializes a `{{key}}` token into HTML must go through here so the shape can
 * never drift between them.
 */
export function buildPlaceholderHtml(key: string, displayLabel: string, fallback?: string): string {
  const labelAttr = displayLabel ? ` data-label="${displayLabel}"` : ''
  const cleanFallback = sanitizeFallback(fallback)
  // The fallback comes from stored HTML text, where `"` is not escaped — it must be before it can
  // sit inside an attribute.
  const fallbackAttr = cleanFallback ? ` data-fb="${cleanFallback.replaceAll('"', '&quot;')}"` : ''
  return `<span class="template-placeholder" data-key="${key}"${labelAttr}${fallbackAttr} contenteditable="false">${displayLabel}</span>${ZERO_WIDTH_SPACE}`
}

/**
 * Rewrites every `{{key}}` / `{{key|fallback}}` token in a string as placeholder-chip HTML. This is the read-only
 * counterpart to editing: the chip is just a `<span class="template-placeholder">`, so surfaces that
 * want to *show* tags (rather than resolve or edit them) can render the result through `HtmlContent`
 * without mounting an editor.
 *
 * `labelMap` supplies author-defined labels; anything missing falls back to a title-cased key.
 */
export function templateToPlaceholderHtml(text: string, labelMap: Record<string, string> = {}): string {
  if (!text) return text
  // Content saved before the change path stripped them can hold runs of zero-width spaces after a
  // token. buildPlaceholderHtml adds exactly one, so drop any already there.
  const withoutStraySpaces = text.replaceAll(/\}\}\u200b+/g, '}}')
  return replaceTemplateTokens(withoutStraySpaces, ({ key, fallback }) =>
    buildPlaceholderHtml(key, formatPlaceholderLabel(key, labelMap[key]), fallback)
  )
}

// Also consumes every zero-width space (ZERO_WIDTH_SPACE) after the chip. The space only exists to
// give the caret a landing spot in the editor; `templateToPlaceholderHtml` adds it back on load.
const PLACEHOLDER_SPAN_REGEX = /<span([^>]*class="[^"]*template-placeholder[^"]*"[^>]*)>(.*?)<\/span>\u200b*/g

/**
 * The inverse of `templateToPlaceholderHtml`: rewrites each chip span in editor HTML as its
 * `{{key}}` / `{{key|fallback}}` token. A legacy chip without `data-key` is keyed from its label via
 * `labelToKey`.
 *
 * The zero-width space after each chip is always stripped. Keeping it on the change path (as the
 * editor once did) made one more pile up after every chip on each save and reload.
 */
export function placeholderHtmlToTemplate(html: string, labelToKey: (label: string) => string): string {
  if (!html) return html
  return html.replaceAll(PLACEHOLDER_SPAN_REGEX, (match, attrs: string, text: string) => {
    const dataKey = attrs.match(/\bdata-key="([^"]+)"/)?.[1]
    const label = text.trim()
    const key = dataKey || (label && !label.includes('<') ? labelToKey(label) : '')
    if (!key) return match
    // getHTML escapes `"` inside attributes; HTML text doesn't need it.
    const fallback = attrs.match(/\bdata-fb="([^"]*)"/)?.[1]?.replaceAll('&quot;', '"')
    return buildTemplateToken(key, fallback)
  })
}

/**
 * Walks the document and returns the position ranges covered by each placeholder
 * (including the trailing zero-width space that anchors the caret outside the
 * placeholder). Shared by the editor's keydown handling and the placeholder
 * protection plugin so they always agree on where a placeholder begins and ends.
 */
export function getPlaceholderRanges(doc: ProsemirrorNode): PlaceholderRange[] {
  const ranges: PlaceholderRange[] = []
  let current: PlaceholderRange | null = null
  doc.descendants((node, nodePos) => {
    if (!node.isText) return
    const mark = node.marks?.find((m) => m.type.name === 'placeholder')
    if (!mark) {
      current = null
      return
    }
    const key = String(mark.attrs?.key ?? mark.attrs?.label ?? 'placeholder')
    const start = nodePos
    const end = nodePos + node.nodeSize
    if (current && current.key === key && start <= current.end) {
      current.end = end
    } else {
      current = { start, end, key, label: mark.attrs?.label ?? null, fallback: mark.attrs?.fallback ?? null }
      ranges.push(current)
    }
  })
  ranges.forEach((range) => {
    // Guard the upper bound: textBetween throws if we read past the end of the doc.
    if (range.end >= doc.content.size) return
    const trailingChar = doc.textBetween(range.end, range.end + 1, '', '')
    if (trailingChar === ZERO_WIDTH_SPACE) {
      range.end += 1
    }
  })
  return ranges
}
